/**
 * CR-7732: the call-off's items as a printed sheet.
 *
 * What someone loading or receiving a lorry needs on paper: the parts, where
 * each is going, how many, how many pallets and what it weighs. No prices —
 * the Items tab dropped them for the same reason, and the people this sheet
 * is for are handling the goods, not the commercial terms.
 *
 * Hidden on screen (`hidden print:block`) and revealed by the @media print
 * rule in `index.css`, which takes every other surface out of layout so the
 * printer sees only this subtree.
 *
 * Rendered through a portal into `document.body` and mounting a
 * `call-off-print-active` marker on body for its lifetime — both load-bearing,
 * for the reasons set out at length in PrintablePOSummary: the "everything
 * except this" selector has to be a direct-child one, the marker keeps the
 * rule from blanking every other screen, and staying in normal flow is what
 * lets the browser paginate a long list across sheets at all.
 */
import { useEffect } from 'react';
import { describeTotal, palletTotal, palletsOf, weightOf, weightTotal } from './callOffTotals';
import { createPortal } from 'react-dom';
import type { CallOff, CallOffLine } from '../../../lib/api';

interface PrintableCallOffItemsProps {
  callOff: CallOff;
  formatDate: (date: string | Date) => string;
  /** Stamped on the footer, so a printed sheet says who ran it. */
  generatedBy: string | null;
}

function destinationOf(line: CallOffLine): string {
  return line.delivery_type === 'warehouse'
    ? line.delivery_destination_name || 'Warehouse'
    : line.delivery_address || line.sales_order || 'Direct';
}


export default function PrintableCallOffItems({
  callOff, formatDate, generatedBy,
}: PrintableCallOffItemsProps) {
  useEffect(() => {
    document.body.classList.add('call-off-print-active');
    return () => document.body.classList.remove('call-off-print-active');
  }, []);

  const lines = callOff.lines ?? [];
  const hasDirect = lines.some(line => line.delivery_type !== 'warehouse');
  // CR-7714: lines with no pallet quantity / unit weight are counted, not added as 0.
  const pallets = palletTotal(lines);
  const weight = weightTotal(lines);
  const totalQty = lines.reduce(
    (sum, line) => sum + (parseFloat(line.requested_qty) || 0), 0,
  );

  return createPortal(
    <div className="printable-call-off hidden print:block">
      <header style={{ marginBottom: '10mm' }}>
        <h1 style={{ fontSize: '16pt', margin: 0 }}>Call-Off {callOff.reference}</h1>
        <table style={{ marginTop: '4mm', width: '100%' }}>
          <tbody>
            <tr>
              <td><strong>Customer:</strong> {callOff.customer_name}</td>
              <td><strong>Supplier:</strong> {callOff.supplier_name}</td>
            </tr>
            <tr>
              <td>
                <strong>Pickup date:</strong>{' '}
                {callOff.pickup_date ? formatDate(callOff.pickup_date) : '—'}
              </td>
              <td><strong>Haulier:</strong> {callOff.haulier_name || 'Not assigned'}</td>
            </tr>
            {callOff.lorry_number ? (
              <tr>
                <td><strong>Lorry:</strong> {callOff.lorry_number}</td>
                <td>
                  <strong>Collection:</strong>{' '}
                  {callOff.haulier_pickup_date
                    ? formatDate(callOff.haulier_pickup_date)
                    : '—'}
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </header>

      <h2 style={{ fontSize: '12pt' }}>Items ({lines.length})</h2>
      <table>
        <thead>
          <tr>
            <th align="left">Part Number</th>
            <th align="left">Description</th>
            <th align="left">Destination</th>
            {hasDirect && <th align="left">Sales Order</th>}
            <th align="right">Requested</th>
            <th align="right">Pallets</th>
            <th align="right">Weight (kg)</th>
          </tr>
        </thead>
        <tbody>
          {lines.map(line => (
            <tr key={line.id}>
              <td>{line.part_number}</td>
              <td>{line.description}</td>
              <td>{destinationOf(line)}</td>
              {hasDirect && <td>{line.sales_order || '—'}</td>}
              <td align="right">{line.requested_qty}</td>
              <td align="right">{palletsOf(line) ?? '—'}</td>
              <td align="right">{weightOf(line)?.toFixed(2) ?? '—'}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={hasDirect ? 4 : 3}><strong>Total</strong></td>
            <td align="right"><strong>{totalQty}</strong></td>
            <td align="right"><strong>{describeTotal(pallets, 'pallet data')}</strong></td>
            <td align="right"><strong>{describeTotal(weight, 'weight data', (n) => n.toFixed(2))}</strong></td>
          </tr>
        </tfoot>
      </table>

      {callOff.notes ? (
        <>
          <h2 style={{ fontSize: '12pt' }}>Notes</h2>
          <p>{callOff.notes}</p>
        </>
      ) : null}

      <footer style={{ marginTop: '10mm', fontSize: '9pt' }}>
        Generated {formatDate(new Date())}
        {generatedBy ? ` by ${generatedBy}` : ''}
      </footer>
    </div>,
    document.body,
  );
}
